import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions,
  initialAllowancesQuestions,
  getAllowanceJobDescriptions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function NebraskaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFirstNameMiddleInitial,
  version,
  formLink,
  isOnboardingV2,
}) {
  const isVersion2020 = version === '12_2020';
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(
    step1InitialQuestions,
  );

  const [forYourself, setForYourself] = useState(data.for_yourself || false);
  const [onlyOneJob, setOnlyOneJob] = useState(data.only_one_job || false);
  const [forSpouse, setForSpouse] = useState(data.for_spouse || false);
  const [fileHeadOfHousehold, setFileHeadOfHousehold] = useState(
    data.file_head_of_household || false,
  );
  const [childOrDependentCareExpenses, setChildOrDependentCareExpenses] = useState(data.child_or_dependent_care_expenses || false);

  const allowancesForm = useRef();
  const allowancesQuestionsWithAnswers = getInitialQuestions(
    initialAllowancesQuestions,
  );
  const [allowancesQuestions, setAllowancesQuestions] = useState(
    allowancesQuestionsWithAnswers,
  );

  const [isExempt, setIsExempt] = useState(data.is_exempt || false);

  // to calculate total number of allowances
  const calculateTotalNoAllowances = () => {
    let value = 0;

    if (forYourself) value += 1;
    if (onlyOneJob) value += 1;
    if (forSpouse) value += 1;
    if (fileHeadOfHousehold) value += 1;
    if (childOrDependentCareExpenses) value += 1;

    const { dependents } = allowancesQuestions[0].answer.answer;
    if (/^\d+$/.test(dependents)) value += parseInt(dependents);

    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions from withholding
      </Text>
      <S.QuestionWrapper>
        <Flex column>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={isExempt}
                checked={isExempt}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExempt(e.target.checked);
                }}
              />
              <Box ml={2} />
              <Flex column>
                <Text mb="8px">
                  Check this box if you claim exemption from withholding and
                  can provide satisfactory evidence to your employer that you
                  meet both of the following conditions for exemption:
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  Last year you had a right to a refund of all
                  Nebraska income tax withheld because you had no tax
                  liability, and
                </Text>
                <Text>
                  {'\u2022'}
                  {' '}
                  This year you expect a refund of all Nebraska
                  income tax withheld because you expect to have no tax
                  liability.
                </Text>
              </Flex>
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Allowances
      </Text>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>Total number of allowances:</S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateTotalNoAllowances()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Check the applicable boxes below to determine your allowances.
          </S.QuestionLabel>
        </Flex>
        <Flex column>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={forYourself}
                  checked={forYourself}
                  disabled={isReviewStep}
                  onChange={(e) => setForYourself(e.target.checked)}
                />
                <Box ml={2} />
                For yourself if no one else can claim you as a dependent.
              </Flex>
            </label>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={onlyOneJob}
                  checked={onlyOneJob}
                  disabled={isReviewStep}
                  onChange={(e) => setOnlyOneJob(e.target.checked)}
                />
                <Box ml={2} />
                <Flex column>
                  <Text mb="8px">If:</Text>
                  {getAllowanceJobDescriptions(version).map((description) => (
                    <Text mb="5px" key={description}>
                      {'\u2022'}
                      {' '}
                      {description}
                    </Text>
                  ))}
                </Flex>
              </Flex>
            </label>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={forSpouse}
                  checked={forSpouse}
                  disabled={isReviewStep}
                  onChange={(e) => setForSpouse(e.target.checked)}
                />
                <Box ml={2} />
                For your spouse. But, you may choose not to check this box if
                you are married and have either a working spouse or more than
                one job. (Not checking this may help you avoid having too
                little tax withheld)
              </Flex>
            </label>
          </Flex>
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={fileHeadOfHousehold}
                  checked={fileHeadOfHousehold}
                  disabled={isReviewStep}
                  onChange={(e) => setFileHeadOfHousehold(e.target.checked)}
                />
                <Box ml={2} />
                If you will file as head of household on your tax return.
              </Flex>
            </label>
          </Flex>
          {isVersion2020 && (
          <Flex column>
            <label>
              <Flex mb="8px">
                <S.Input
                  style={{ height: '22px' }}
                  type="checkbox"
                  value={childOrDependentCareExpenses}
                  checked={childOrDependentCareExpenses}
                  disabled={isReviewStep}
                  onChange={(e) => setChildOrDependentCareExpenses(e.target.checked)}
                />
                <Box ml={2} />
                If you have at least $2,000 of child or dependent care
                expenses for which you plan to claim a credit.
              </Flex>
            </label>
          </Flex>
          )}
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={allowancesQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderExemptionsFromWithholding()}
          {!isExempt && renderAllowances()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              let forYourselfTemp = false;
              let onlyOneJobTemp = false;
              let forSpouseTemp = false;
              let fileAsHeadOfHouseholdTemp = false;
              let careExpensesTemp = false;
              let totalNumAllowancesTemp = '';
              let allowancesData = resetInitialQuestions(
                initialAllowancesQuestions,
              ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              if (!isExempt) {
                forYourselfTemp = forYourself;
                onlyOneJobTemp = onlyOneJob;
                forSpouseTemp = forSpouse;
                fileAsHeadOfHouseholdTemp = fileHeadOfHousehold;
                careExpensesTemp = childOrDependentCareExpenses;
                totalNumAllowancesTemp = calculateTotalNoAllowances().toString();
                allowancesData = allowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              }
              const allowancesCheckboxData = {
                for_yourself: forYourselfTemp,
                only_one_job: onlyOneJobTemp,
                for_spouse: forSpouseTemp,
                file_head_of_household: fileAsHeadOfHouseholdTemp,
                child_or_dependent_care_expenses: careExpensesTemp,
                total_number_of_allowances: totalNumAllowancesTemp,
              };

              if (!isVersion2020) {
                delete allowancesCheckboxData.child_or_dependent_care_expenses;
              }

              const exemptionData = { is_exempt: isExempt };
              const data = {
                ...stepOneData,
                ...allowancesCheckboxData,
                ...allowancesData,
                ...exemptionData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderExemptionsFromWithholding(true)}
        {!isExempt && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default NebraskaForm;
